/**
 * Single-flight PKCE code exchange on web OAuth callback.
 * Authorization codes are single-use; detectSessionInUrl + manual exchange both firing causes invalid_grant.
 */

import type { AuthError } from '@supabase/supabase-js'
import { supabase } from '../../lib/supabase'
import {
  claimOAuthPkceExchangeLock,
  extractOAuthAuthCodeFromCallbackUrl,
  releaseOAuthPkceExchangeLock,
} from './oauthPkceExchangeLock.helper'

export type OAuthPkceExchangeOnceResult = {
  ok: boolean
  skipped: boolean
  error: AuthError | null
}

export {
  claimOAuthPkceExchangeLock,
  extractOAuthAuthCodeFromCallbackUrl,
  releaseOAuthPkceExchangeLock,
} from './oauthPkceExchangeLock.helper'

let inFlightExchange: Promise<OAuthPkceExchangeOnceResult> | null = null
let inFlightAuthCode: string | null = null

async function runPkceExchange(callbackUrl: string): Promise<OAuthPkceExchangeOnceResult> {
  const { error } = await supabase.auth.exchangeCodeForSession(callbackUrl)
  if (error) {
    releaseOAuthPkceExchangeLock()
    return { ok: false, skipped: false, error }
  }
  return { ok: true, skipped: false, error: null }
}

/**
 * Exchanges the PKCE auth code at most once per code value (sessionStorage + in-flight dedupe).
 */
export async function exchangeOAuthCodeForSessionOnce(
  callbackUrl: string,
): Promise<OAuthPkceExchangeOnceResult> {
  const authCode = extractOAuthAuthCodeFromCallbackUrl(callbackUrl)
  if (!authCode) {
    return { ok: false, skipped: true, error: null }
  }

  if (inFlightExchange && inFlightAuthCode === authCode) {
    return inFlightExchange
  }

  if (!claimOAuthPkceExchangeLock(authCode)) {
    const { data: { session } } = await supabase.auth.getSession()
    return { ok: !!session, skipped: true, error: null }
  }

  inFlightAuthCode = authCode
  inFlightExchange = runPkceExchange(callbackUrl)
  try {
    return await inFlightExchange
  } finally {
    inFlightExchange = null
    inFlightAuthCode = null
  }
}
